/**
 * Beauty Bookings Wrapper
 *
 * Reuses the existing Booking.tsx system but routes API calls to /api/beauty/bookings
 * This component is a thin wrapper that provides the existing booking calendar & form
 * but scoped to beauty suite only.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Calendar, dateFnsLocalizer, ToolbarProps, View } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { apiFetch } from '../../utils/apiFetch';

const locales = { 'en-GB': require('date-fns/locale/en-GB') };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const CalendarToolbar = ({ label, onNavigate, onView, view }: ToolbarProps) => {
  const views: View[] = ['month', 'week', 'day', 'agenda'];
  const viewLabels: Record<string, string> = { month: 'Month', week: 'Week', day: 'Day', agenda: 'Agenda' };
  const btnBase: React.CSSProperties = {
    padding: '5px 12px', borderRadius: 6, border: '1px solid #e5e7eb',
    background: '#fff', color: '#374151', fontWeight: 600, fontSize: 12,
    cursor: 'pointer', fontFamily: 'inherit',
  };
  const activeView: React.CSSProperties = { background: '#ec4899', color: '#fff', borderColor: '#ec4899' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
      <button style={btnBase} onClick={() => onNavigate('TODAY')}>Today</button>
      <button style={btnBase} onClick={() => onNavigate('PREV')}>‹ Back</button>
      <button style={btnBase} onClick={() => onNavigate('NEXT')}>Next ›</button>
      <span style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 14, color: '#111827', padding: '0 8px' }}>{label}</span>
      {views.map(v => (
        <button key={v} style={{ ...btnBase, ...(view === v ? activeView : {}) }} onClick={() => onView(v)}>
          {viewLabels[v]}
        </button>
      ))}
    </div>
  );
};

const STATUS: Record<string, { bg: string; text: string; dot: string }> = {
  Pending:   { bg: '#fff7e6', text: '#b45309', dot: '#f59e0b' },
  Confirmed: { bg: '#f0fdf4', text: '#166534', dot: '#22c55e' },
  Completed: { bg: '#eff6ff', text: '#1e40af', dot: '#3b82f6' },
  Cancelled: { bg: '#fef2f2', text: '#991b1b', dot: '#ef4444' },
};
const getStatus = (s: string) => STATUS[s] || STATUS.Pending;

type BookingRow = { id: string; service?: string; date?: string; time?: string; status: string; notes?: string; customer_name?: string; staff_name?: string; start?: string; start_time?: string; startTime?: string; };

const PINK = '#ec4899';

export default function BeautyBookings() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'month' | 'week' | 'day' | 'agenda'>('week');
  const [date, setDate] = useState(new Date());
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    customer_name: '', service: '', date: '', start_time: '', duration: '30', staff_name: '', status: 'Pending', notes: '',
  });

  const beautyApiUrl = `${API_URL}/beauty`; // Use beauty-scoped API

  const fetchBookings = async () => {
    if (!token) return;
    try {
      const res = await apiFetch(`${beautyApiUrl}/appointments`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setBookings(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error('Fetch bookings error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [token]);

  const saveBooking = async () => {
    if (!formData.customer_name || !formData.service || !formData.date || !formData.start_time) {
      toast.error('Missing required fields');
      return;
    }
    try {
      const url = editingId ? `${beautyApiUrl}/appointments/${editingId}` : `${beautyApiUrl}/appointments`;
      const res = await apiFetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          client_name: formData.customer_name,
          service_name: formData.service,
          date: formData.date,
          start_time: `${formData.date}T${formData.start_time}:00`,
          duration_mins: Number(formData.duration),
          staff_name: formData.staff_name,
          status: formData.status,
          notes: formData.notes,
        }),
      });
      if (res.ok) {
        toast.success(editingId ? 'Updated!' : 'Created!');
        setShowForm(false);
        setEditingId(null);
        setFormData({ customer_name: '', service: '', date: '', start_time: '', duration: '30', staff_name: '', status: 'Pending', notes: '' });
        fetchBookings();
      } else {
        const err = await res.json();
        toast.error(err.error || 'Save failed');
      }
    } catch (e: any) {
      toast.error(e.message || 'Error saving');
    }
  };

  const deleteBooking = async (id: string) => {
    if (!window.confirm('Delete this booking?')) return;
    try {
      await apiFetch(`${beautyApiUrl}/appointments/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      fetchBookings();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const calendarEvents = useMemo(() =>
    bookings.map(b => ({
      id: b.id,
      title: `${b.customer_name} - ${b.service}`,
      start: new Date(b.start_time || b.start || ''),
      end: new Date((b.start_time || b.start || '').replace(/T\d{2}:\d{2}/, `T${String(Number(b.start_time?.split('T')[1]?.split(':')[0] || 0) + Number(b.time || 0)).padStart(2, '0')}:00`)),
      resource: b,
    })).filter(e => !isNaN(e.start.getTime())),
    [bookings]
  );

  return (
    <div style={{ padding: '28px 32px', fontFamily: "'DM Sans', system-ui, sans-serif", maxWidth: 1400, margin: '0 auto' }}>
      <ToastContainer position="top-right" hideProgressBar />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#111827' }}>📅 Appointments</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>{bookings.length} total appointments</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditingId(null); setFormData({ customer_name: '', service: '', date: '', start_time: '', duration: '30', staff_name: '', status: 'Pending', notes: '' }); }}
          style={{ background: PINK, color: '#fff', border: 'none', borderRadius: 10, padding: '10px 22px', fontWeight: 700, cursor: 'pointer' }}>
          + New Appointment
        </button>
      </div>

      {/* Calendar */}
      {!showForm && (
        <div style={{ background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 24 }}>
          <CalendarToolbar label={date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })} onNavigate={d => setDate(d)} onView={setView} view={view} />
          <Calendar
            localizer={localizer}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%', minHeight: 500 }}
            view={view}
            onView={setView}
            date={date}
            onNavigate={setDate}
            components={{ toolbar: () => null }}
            eventPropGetter={e => ({ style: { background: PINK, color: '#fff', borderRadius: 6, padding: '2px 6px', fontSize: 12, fontWeight: 600 } })}
            onSelectEvent={e => {
              const b = e.resource;
              setEditingId(b.id);
              setFormData({ customer_name: b.customer_name || '', service: b.service || '', date: b.date || '', start_time: b.start_time?.split('T')[1]?.slice(0, 5) || '', duration: b.time || '30', staff_name: b.staff_name || '', status: b.status || 'Pending', notes: b.notes || '' });
              setShowForm(true);
            }}
          />
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div style={{ background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 24 }}>
          <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700 }}>{editingId ? 'Edit' : 'New'} Appointment</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <input placeholder="Client Name *" value={formData.customer_name} onChange={e => setFormData({...formData, customer_name: e.target.value})} style={{ padding: 10, border: `1px solid #ddd`, borderRadius: 8, fontFamily: 'inherit' }} />
            <input placeholder="Service *" value={formData.service} onChange={e => setFormData({...formData, service: e.target.value})} style={{ padding: 10, border: `1px solid #ddd`, borderRadius: 8, fontFamily: 'inherit' }} />
            <input type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} style={{ padding: 10, border: `1px solid #ddd`, borderRadius: 8, fontFamily: 'inherit' }} />
            <input type="time" value={formData.start_time} onChange={e => setFormData({...formData, start_time: e.target.value})} style={{ padding: 10, border: `1px solid #ddd`, borderRadius: 8, fontFamily: 'inherit' }} />
            <input placeholder="Staff" value={formData.staff_name} onChange={e => setFormData({...formData, staff_name: e.target.value})} style={{ padding: 10, border: `1px solid #ddd`, borderRadius: 8, fontFamily: 'inherit' }} />
            <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} style={{ padding: 10, border: `1px solid #ddd`, borderRadius: 8, fontFamily: 'inherit' }}>
              <option>Pending</option>
              <option>Confirmed</option>
              <option>Completed</option>
              <option>Cancelled</option>
            </select>
          </div>
          <textarea placeholder="Notes" value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} style={{ width: '100%', padding: 10, border: `1px solid #ddd`, borderRadius: 8, fontFamily: 'inherit', minHeight: 80, marginBottom: 16 }} />
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={saveBooking} style={{ background: PINK, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 600, cursor: 'pointer' }}>Save</button>
            <button onClick={() => setShowForm(false)} style={{ background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            {editingId && <button onClick={() => deleteBooking(editingId)} style={{ background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 600, cursor: 'pointer', marginLeft: 'auto' }}>Delete</button>}
          </div>
        </div>
      )}

      {/* List View */}
      {!showForm && (
        <div style={{ background: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: `2px solid ${PINK}` }}>
                {['Date', 'Time', 'Client', 'Service', 'Staff', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bookings.map((b, i) => {
                const st = getStatus(b.status);
                return (
                  <tr key={b.id} style={{ borderTop: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>{b.date || b.start?.split('T')[0] || '—'}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>{b.time || b.start_time?.split('T')[1]?.slice(0, 5) || '—'}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#111827', fontWeight: 500 }}>{b.customer_name || '—'}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280' }}>{b.service || '—'}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280' }}>{b.staff_name || '—'}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ background: st.bg, color: st.text, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>{b.status}</span>
                    </td>
                    <td style={{ padding: '12px 16px', display: 'flex', gap: 6 }}>
                      <button onClick={() => { setEditingId(b.id); setFormData({ customer_name: b.customer_name || '', service: b.service || '', date: b.date || '', start_time: b.start_time?.split('T')[1]?.slice(0, 5) || '', duration: b.time || '30', staff_name: b.staff_name || '', status: b.status || 'Pending', notes: '' }); setShowForm(true); }} style={{ background: 'transparent', color: PINK, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>Edit</button>
                      <button onClick={() => deleteBooking(b.id)} style={{ background: 'transparent', color: '#ef4444', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
